import { useIsWeb } from '@/hooks/useIsWeb';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Image } from 'react-native';
import { YStack, useTheme } from 'tamagui';
import { isImageFile } from '../speciesSearch.utils';
import {
  PREVIEW_BORDER_WIDTH,
  PREVIEW_SIZE_IDLE,
  PREVIEW_SIZE_LOADING,
  PULSE_ANIMATION_DURATION_MS,
  PULSE_ANIMATION_STAGGER_MS,
  PULSE_RING_COUNT,
  PULSE_RING_SIZE,
  PULSE_SCALE_END,
  PULSE_SCALE_START,
} from './speciesAiSearch.constants';

interface SpeciesAiPreviewProps {
  fileUri?: string;
  fileType?: string;
  fileLabel?: string;
  isLoading: boolean;
}

export const SpeciesAiPreview = ({
  fileUri,
  fileType,
  fileLabel,
  isLoading,
}: SpeciesAiPreviewProps): React.ReactElement => {
  const theme = useTheme();
  const isWeb = useIsWeb();
  const brandColor = theme.brand?.val ?? theme.color.val;
  const ringColor = theme.brand70?.val ?? theme.color.val;
  const pulseValues = useRef(
    Array.from({ length: PULSE_RING_COUNT }, () => new Animated.Value(0)),
  ).current;
  const showPreview = Boolean(fileUri);
  const isImagePreview =
    showPreview && isImageFile(fileUri, fileType, fileLabel);
  const previewSize = isLoading ? PREVIEW_SIZE_LOADING : PREVIEW_SIZE_IDLE;
  const previewRadius = previewSize / 2;
  const containerSize = isLoading ? PULSE_RING_SIZE : previewSize;

  useEffect(() => {
    if (!showPreview) {
      return;
    }

    const animations = pulseValues.map((value, index) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(index * PULSE_ANIMATION_STAGGER_MS),
          Animated.timing(value, {
            toValue: 1,
            duration: PULSE_ANIMATION_DURATION_MS,
            easing: Easing.out(Easing.quad),
            useNativeDriver: !isWeb,
          }),
          Animated.timing(value, {
            toValue: 0,
            duration: 0,
            useNativeDriver: !isWeb,
          }),
        ]),
      ),
    );

    animations.forEach((animation) => animation.start());

    return (): void => {
      animations.forEach((animation) => animation.stop());
    };
  }, [isWeb, pulseValues, showPreview]);

  return (
    <YStack
      alignItems="center"
      justifyContent="center"
      width={containerSize}
      height={containerSize}
      position="relative"
    >
      {isLoading &&
        pulseValues.map((value, index) => {
          const ringOpacity = value.interpolate({
            inputRange: [0, 1],
            outputRange: [0.35, 0],
          });
          const ringScale = value.interpolate({
            inputRange: [0, 1],
            outputRange: [PULSE_SCALE_START, PULSE_SCALE_END],
          });

          return (
            <Animated.View
              key={`pulse-${index}`}
              pointerEvents="none"
              style={{
                position: 'absolute',
                width: PULSE_RING_SIZE,
                height: PULSE_RING_SIZE,
                borderRadius: PULSE_RING_SIZE / 2,
                borderWidth: PREVIEW_BORDER_WIDTH,
                borderColor: ringColor,
                opacity: ringOpacity,
                transform: [{ scale: ringScale }],
              }}
            />
          );
        })}

      <YStack
        width={previewSize}
        height={previewSize}
        borderRadius={previewRadius}
        alignItems="center"
        justifyContent="center"
        backgroundColor="$background"
        borderColor="$tertiary75"
        borderWidth={PREVIEW_BORDER_WIDTH}
        overflow="hidden"
        shadowColor="$shadow"
        shadowOpacity={0.2}
        shadowRadius={12}
        shadowOffset={{ width: 0, height: 6 }}
        elevation={6}
      >
        {isImagePreview && fileUri ? (
          <Image
            source={{ uri: fileUri }}
            resizeMode="cover"
            style={{
              width: previewSize,
              height: previewSize,
              borderRadius: previewRadius,
            }}
          />
        ) : (
          <YStack
            width={previewSize}
            height={previewSize}
            alignItems="center"
            justifyContent="center"
            backgroundColor="$tertiary50"
          >
            <Ionicons
              name="document-text-outline"
              size={48}
              color={brandColor}
            />
          </YStack>
        )}
      </YStack>
    </YStack>
  );
};
